import { useMemo } from 'react';
import { Button, SxProps, Theme } from '@mui/material';

import styles from '@/components/buttons/Button.module.css';

export enum GlobalButtonVariant {
  primaryButton = 'contained',
  secondaryButton = 'outlined',
  textButton = 'text',
}

export interface GlobalButtonProps {
  variant: GlobalButtonVariant;
  action: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  sx?: SxProps<Theme>;
}

const GlobalButton = ({
  variant,
  action,
  children,
  disabled,
  size = 'medium',
  className,
  sx,
}: GlobalButtonProps) => {
  const internalSx: SxProps<Theme> = useMemo(() => {
    if (variant === GlobalButtonVariant.secondaryButton) {
      return {
        borderColor: 'var(--primary-color)',
        '&:hover': {
          borderColor: '#FF560E',
        },
      };
    }
    if (variant === GlobalButtonVariant.textButton) {
      return {
        '&:hover': {
          textDecoration: 'underline',
        },
      };
    }
    return {};
  }, [variant]);

  return (
    <Button
      className={className}
      variant={variant}
      onClick={action}
      disabled={disabled}
      size={size}
      color={
        [
          GlobalButtonVariant.textButton,
          GlobalButtonVariant.primaryButton,
        ].includes(variant)
          ? 'primary'
          : 'secondary'
      }
      sx={[
        { cursor: 'pointer', '&.Mui-disabled': { cursor: 'not-allowed' } },
        ...(Array.isArray(internalSx) ? internalSx : [internalSx]),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <div className={styles.buttonContent}>{children}</div>
    </Button>
  );
};

export default GlobalButton;
